import { createContext, useEffect, useRef, useState, type PropsWithChildren } from 'react'
import useSWR from 'swr'
import { toast } from 'sonner'
import { API_CONFIG } from '@/config/api'

interface HealthResponse {
  status: string
  database: boolean
  version: string
  commit?: string
}

const CHECK_INTERVAL = 5 * 60 * 1000 // Check every 5 minutes

interface AppVersionContextType {
  version: string | null
  commit: string | null
  isLoading: boolean
  error: any
  enableVersionCheck: boolean
  setEnableVersionCheck: (value: boolean) => void
}

export const AppVersionContext = createContext<AppVersionContextType | null>(null)

export function AppVersionProvider({ children }: PropsWithChildren) {
  const [enableVersionCheck, setEnableVersionCheck] = useState(true)
  const initialVersion = useRef<string | null>(null)
  const hasNotified = useRef(false)

  // Health endpoint doesn't require authentication
  const fetcher = async (url: string): Promise<HealthResponse> => {
    const response = await fetch(url, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' },
    })
    if (!response.ok) {
      throw new Error('Failed to fetch version')
    }
    return response.json()
  }

  const { data, error, isLoading } = useSWR<HealthResponse>(`${API_CONFIG.BASE_URL}/health`, fetcher, {
    revalidateOnFocus: false,
    refreshInterval: enableVersionCheck ? CHECK_INTERVAL : 0, // Poll version checking
  })

  // Version checking logic
  useEffect(() => {
    if (!enableVersionCheck || !data?.version) {
      return
    }

    // Skip version checking in development (can be commented out for testing)
    // TEMPORARY: Commented out for local testing
    // if (import.meta.env.DEV) {
    //   return
    // }

    // Store the initial version on first successful fetch
    if (initialVersion.current === null) {
      initialVersion.current = data.version
      console.debug('[Version Check] Initial version:', data.version)
      return
    }

    // If versions don't match and we haven't notified yet
    if (data.version !== initialVersion.current && !hasNotified.current) {
      hasNotified.current = true
      console.info(`[Version Check] New version detected: ${initialVersion.current} → ${data.version}`)

      // Show persistent toast with action
      toast.info('A new version is available', {
        description: 'Click refresh to update to the latest version.',
        duration: Number.POSITIVE_INFINITY, // Keep toast visible
        action: {
          label: 'Refresh',
          onClick: () => {
            window.location.reload()
          },
        },
        onDismiss: () => {
          // Allow notification again after 1 hour if user dismisses
          setTimeout(
            () => {
              hasNotified.current = false
            },
            60 * 60 * 1000
          )
        },
      })
    }
  }, [data?.version, enableVersionCheck])

  const value = {
    version: data?.version || null,
    commit: data?.commit || null,
    isLoading,
    error,
    enableVersionCheck,
    setEnableVersionCheck,
  }

  return <AppVersionContext.Provider value={value}>{children}</AppVersionContext.Provider>
}
